import { toMinorUnits } from '../currency';

export interface KioskPriceConfig {
  currency: string;
  groupPhotoCents: number;
  portraitCents: number;
}

export type KioskPriceConfigResult =
  | { ok: true; value: KioskPriceConfig }
  | { ok: false; errors: string[] };

export interface KioskPriceConfigInput {
  strict: boolean;
  currency: string | null;
  portraitPriceMajor: number | null;
  groupPhotoPriceMajor: number | null;
  fallback: KioskPriceConfig;
}

export interface KioskPrinterConfig {
  verticalPrinterId: string;
  horizontalPrinterId: string;
}

export interface KioskPrinterConfigInput {
  printerId?: string | null;
  verticalPrinterId?: string | null;
  horizontalPrinterId?: string | null;
}

export type KioskPaymentDestination = 'platform' | 'partner';

export interface KioskPaymentRuntimeConfig {
  terminalPaymentEnabled: boolean;
  readerId: string | null;
  paymentDestination?: KioskPaymentDestination | string | null;
  stripeAccountId?: string | null;
}

export interface KioskRuntimeConfig {
  kioskSlug?: string | null;
  prices: KioskPriceConfig;
  printer: KioskPrinterConfig | null;
  payment: KioskPaymentRuntimeConfig;
}

export interface KioskRuntimeStartupAuditInput {
  strict: boolean;
  priceConfig: KioskPriceConfigResult;
  printTransport?: string;
  printer: KioskPrinterConfig | null;
  payment: KioskPaymentRuntimeConfig;
}

export interface KioskRuntimeStartupAuditResult {
  ok: boolean;
  errors: string[];
}

export function resolveKioskPriceConfig(
  input: KioskPriceConfigInput
): KioskPriceConfigResult {
  const currency = configuredValue(input.currency);

  if (!input.strict) {
    const resultCurrency = currency ?? input.fallback.currency;
    return {
      ok: true,
      value: {
        currency: resultCurrency,
        portraitCents:
          majorToMinorUnits(input.portraitPriceMajor, resultCurrency) ??
          input.fallback.portraitCents,
        groupPhotoCents:
          majorToMinorUnits(input.groupPhotoPriceMajor, resultCurrency) ??
          input.fallback.groupPhotoCents,
      },
    };
  }

  const portraitCents = majorToMinorUnits(input.portraitPriceMajor, currency);
  const groupPhotoCents =
    majorToMinorUnits(input.groupPhotoPriceMajor, currency) ?? portraitCents;
  const errors: string[] = [];
  if (!currency) errors.push('kiosk config currency is required');
  if (portraitCents === null)
    errors.push('kiosk config portrait price is required');
  if (groupPhotoCents === null) {
    errors.push('kiosk config group photo price is required');
  }

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    value: {
      currency: currency as string,
      portraitCents: portraitCents as number,
      groupPhotoCents: groupPhotoCents as number,
    },
  };
}

export function resolveKioskPrinterConfig(
  input: KioskPrinterConfigInput
): KioskPrinterConfig | null {
  const verticalPrinterId = configuredString(input.verticalPrinterId);
  const horizontalPrinterId = configuredString(input.horizontalPrinterId);
  if (verticalPrinterId || horizontalPrinterId) {
    const sharedPrinterId = verticalPrinterId ?? horizontalPrinterId;
    if (!sharedPrinterId) return null;
    return {
      verticalPrinterId: verticalPrinterId ?? sharedPrinterId,
      horizontalPrinterId: horizontalPrinterId ?? sharedPrinterId,
    };
  }

  const printerId = configuredString(input.printerId);
  if (!printerId) return null;
  return {
    verticalPrinterId: printerId,
    horizontalPrinterId: printerId,
  };
}

export function auditKioskRuntimeStartupConfig(
  input: KioskRuntimeStartupAuditInput
): KioskRuntimeStartupAuditResult {
  if (!input.strict) return { ok: true, errors: [] };

  const errors = input.priceConfig.ok ? [] : [...input.priceConfig.errors];
  if (input.printTransport === 'kiosk' && !input.printer) {
    errors.push('kiosk print transport requires a printer');
  }
  if (
    input.payment.terminalPaymentEnabled &&
    !configuredValue(input.payment.readerId)
  ) {
    errors.push('enabled payments require a Stripe reader id');
  }
  if (
    input.payment.terminalPaymentEnabled &&
    input.payment.paymentDestination?.trim().toLowerCase() === 'partner' &&
    !configuredString(input.payment.stripeAccountId)
  ) {
    errors.push('partner payments require a Stripe account id');
  }

  return { ok: errors.length === 0, errors };
}

function majorToMinorUnits(
  value: number | null,
  currency: string | null
): number | null {
  if (value === null || !Number.isFinite(value) || value < 0) return null;
  return toMinorUnits(value, currency);
}

function configuredValue(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed.toUpperCase() : null;
}

export function configuredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}
